import React, { useEffect, useState } from 'react';
import BarcodeScanner from '../BarcodeScanner';

const BOTTLE_MESSAGE_INTERACTION_RADIUS_M = 30;

function formatDistance(distanceMeters) {
  if (!Number.isFinite(distanceMeters)) {
    return 'Entfernung unbekannt';
  }

  return `${Math.round(distanceMeters)} m entfernt`;
}

function formatDate(value) {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).replace(' ', 'T');
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const normalizedValue = String(value).replace(' ', 'T');
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseDateMs(value) {
  if (!value) {
    return null;
  }

  const date = new Date(String(value).replace(' ', 'T'));
  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatRelativeDateTime(value) {
  const timestamp = parseDateMs(value);
  if (!timestamp) {
    return '';
  }

  const diffMs = Date.now() - timestamp;
  if (diffMs < 0) {
    return '';
  }

  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) {
    return 'gerade eben';
  }

  if (diffMinutes < 60) {
    return diffMinutes === 1 ? 'vor 1 Minute' : `vor ${diffMinutes} Minuten`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return diffHours === 1 ? 'vor 1 Stunde' : `vor ${diffHours} Stunden`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? 'vor 1 Tag' : `vor ${diffDays} Tagen`;
}

function shouldShowModifiedAt(item) {
  const createdAtMs = parseDateMs(item?.created_at || item?.date);
  const modifiedAtMs = parseDateMs(item?.modified_at || item?.modified);

  return Boolean(createdAtMs && modifiedAtMs && Math.abs(modifiedAtMs - createdAtMs) > 60000);
}

function formatConfirmationCount(value) {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) {
    return 'Noch nicht bestätigt';
  }

  return count === 1 ? '1 Bestätigung' : `${count} Bestätigungen`;
}

function isGiveawayDonation(item) {
  return item?.sourceType === 'donation' && (item?.item_type === 'giveaway' || item?.pfand_type === 'zu_verschenken');
}

function getDonationLabel(item) {
  return isGiveawayDonation(item) ? 'Zu verschenken' : 'Pfandspende';
}

function getLocationLabel(item) {
  return item?.lootLabel || (item?.isAutoGenerated ? 'Vorgeschlagener Loot-Ort' : 'Ort');
}

function buildNavigationUrl(item) {
  const lat = Number(item?.latitude ?? item?.lat);
  const lng = Number(item?.longitude ?? item?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return '';
  }

  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function renderLootFeedback(item, lootMessage) {
  if (!lootMessage || String(lootMessage.locationId) !== String(item?.id)) {
    return null;
  }

  if (lootMessage.success) {
    return (
      <div className="treasure-map-details__loot-box is-success">
        {lootMessage.icon && <img src={lootMessage.icon} alt="" />}
        <div>Du hast gefunden:</div>
        <strong>{lootMessage.name}</strong>
        <div className="treasure-map-details__loot-rarity">{lootMessage.rarity}</div>
      </div>
    );
  }

  if (lootMessage.error) {
    return <div className="treasure-map-details__loot-box is-error">{lootMessage.error}</div>;
  }

  return null;
}

function LocationActions({ item, canLoot, getCooldownTime, formatCooldown, handleLoot }) {
  if (!item) {
    return null;
  }

  if (typeof canLoot === 'function' && canLoot(item)) {
    return (
      <button type="button" className="treasure-map-details__primary-action" onClick={() => handleLoot?.(item)}>
        💰 Jetzt looten
      </button>
    );
  }

  const cooldownMs = typeof getCooldownTime === 'function' ? Number(getCooldownTime(item.id) || 0) : 0;
  if (cooldownMs > 0) {
    return (
      <div className="treasure-map-details__status is-warning">
        Cooldown aktiv · noch {formatCooldown?.(cooldownMs)}
      </div>
    );
  }

  return (
    <div className="treasure-map-details__status">
      Für Loot musst du näher an den Ort herangehen.
    </div>
  );
}

function normalizeEvidenceBarcode(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 14);
}

function isValidEvidenceBarcode(value) {
  return /^\d{8,14}$/.test(String(value || ''));
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function isPickupBarcodeRequired(item) {
  if (isGiveawayDonation(item)) {
    return false;
  }

  if (item?.requires_pickup_barcode !== undefined) {
    return isTruthyFlag(item.requires_pickup_barcode);
  }

  return Number(item?.event_id || item?.event?.id || item?.challenge_event?.id || 0) > 0;
}

function DonationActions({ item, handlePfandAbgeholt }) {
  const [showEvidence, setShowEvidence] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setShowEvidence(false);
    setShowScanner(false);
    setBarcode('');
    setPhotoFile(null);
    setError('');
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  useEffect(() => () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
  }, [photoPreview]);

  if (!item) {
    return null;
  }

  if (typeof handlePfandAbgeholt === 'function') {
    const barcodeRequired = isPickupBarcodeRequired(item);
    const showBarcodeField = barcodeRequired || !isGiveawayDonation(item);
    const photoRequired = barcodeRequired;
    const evidenceHint = barcodeRequired
      ? 'Challenge: EAN scannen und Foto aufnehmen.'
      : isGiveawayDonation(item)
        ? 'Optional ein Foto ergänzen oder direkt bestätigen.'
        : 'Optional EAN oder Foto ergänzen.';

    const handlePhotoChange = (event) => {
      const file = event.target.files?.[0] || null;
      setError('');

      if (!file) {
        setPhotoFile(null);
        if (photoPreview) {
          URL.revokeObjectURL(photoPreview);
          setPhotoPreview('');
        }
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        setError('Bitte ein Foto auswählen.');
        return;
      }

      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }

      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    };

    const submitEvidence = async () => {
      const normalizedBarcode = normalizeEvidenceBarcode(barcode);
      if (barcodeRequired && !isValidEvidenceBarcode(normalizedBarcode)) {
        setError('Für Challenge-Abholungen bitte erst einen EAN-Code scannen oder eingeben.');
        return;
      }

      if (!barcodeRequired && normalizedBarcode && !isValidEvidenceBarcode(normalizedBarcode)) {
        setError('Der EAN-Code ist optional. Wenn du ihn einträgst, bitte vollständig mit 8 bis 14 Ziffern.');
        return;
      }

      if (photoRequired && !photoFile) {
        setError('Bitte ein Foto der Abholung aufnehmen.');
        return;
      }

      setSubmitting(true);
      setError('');

      const result = await handlePfandAbgeholt(item, {
        barcode: normalizedBarcode,
        photo: photoFile,
      });

      setSubmitting(false);
      if (result?.ok) {
        setShowEvidence(false);
      } else if (result?.message) {
        setError(result.message);
      }
    };

    return (
      <div className="treasure-map-details__pickup-evidence">
        {!showEvidence ? (
          <button type="button" className="treasure-map-details__primary-action" onClick={() => setShowEvidence(true)}>
            Abholung bestätigen
          </button>
        ) : (
          <div className="treasure-map-details__evidence-card">
            <div className="treasure-map-details__evidence-head">
              <strong>Abholung nachweisen</strong>
              <span>{evidenceHint}</span>
            </div>

            {showBarcodeField ? (
              <>
                {showScanner ? (
                  <div className="treasure-map-details__scanner-box">
                    <BarcodeScanner
                      onDetected={(code) => {
                        setBarcode(normalizeEvidenceBarcode(code));
                        setShowScanner(false);
                        setError('');
                      }}
                      onClose={() => setShowScanner(false)}
                    />
                  </div>
                ) : (
                  <button type="button" className="treasure-map-details__secondary-action" onClick={() => setShowScanner(true)}>
                    EAN scannen
                  </button>
                )}

                <label className="treasure-map-details__field">
                  <span>{barcodeRequired ? 'EAN-Code (Pflicht)' : 'EAN-Code (optional)'}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={barcode}
                    onChange={(event) => setBarcode(normalizeEvidenceBarcode(event.target.value))}
                    placeholder="z. B. 4001234567890"
                  />
                </label>
              </>
            ) : null}

            <label className={`treasure-map-details__photo-picker ${photoPreview ? 'has-preview' : ''}`}>
              <input type="file" accept="image/*" onChange={handlePhotoChange} />
              {photoPreview ? (
                <img src={photoPreview} alt="Abholfoto-Vorschau" />
              ) : (
                <span>{photoRequired ? 'Foto der Abholung aufnehmen' : 'Foto der Abholung aufnehmen (optional)'}</span>
              )}
            </label>

            {error ? <div className="treasure-map-details__status is-warning">{error}</div> : null}

            <div className="treasure-map-details__evidence-actions">
              <button type="button" className="treasure-map-details__secondary-action" onClick={() => setShowEvidence(false)} disabled={submitting}>
                Abbrechen
              </button>
              <button type="button" className="treasure-map-details__primary-action" onClick={submitEvidence} disabled={submitting}>
                {submitting ? 'Wird bestätigt ...' : 'Abholung abschicken'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (item?.link) {
    return (
      <a
        className="treasure-map-details__primary-action treasure-map-details__primary-action--link"
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
      >
        Zu den Details
      </a>
    );
  }

  return null;
}

function BottleMessageActions({
  item,
  busyBottleMessageId,
  openedBottleMessages,
  onOpenBottleMessage,
  canLoot,
  getCooldownTime,
  formatCooldown,
  handleLoot,
}) {
  if (!item) {
    return null;
  }

  const openedState = openedBottleMessages?.[item.id] || item.openedState || null;
  const isBusy = Number(busyBottleMessageId) === Number(item.id);
  const distance = Number(item?.distanceMeters || Number.MAX_SAFE_INTEGER);
  const isNear = Number.isFinite(distance) && distance <= BOTTLE_MESSAGE_INTERACTION_RADIUS_M;
  const virtualLocation = openedState?.location || {
    id: item.location_id || `bottle-message-${item.id}`,
    latitude: item.latitude,
    longitude: item.longitude,
    lat: item.lat,
    lng: item.lng,
    lootRadiusM: BOTTLE_MESSAGE_INTERACTION_RADIUS_M,
  };

  if (!isNear) {
    return <div className="treasure-map-details__status">Du bist zu weit entfernt, um die Flaschenpost zu öffnen oder dort zu looten.</div>;
  }

  if (!openedState?.success) {
    return (
      <>
        {openedState?.error && <div className="treasure-map-details__status is-warning">{openedState.error}</div>}
        <button
          type="button"
          className="treasure-map-details__primary-action"
          onClick={() => onOpenBottleMessage?.(item)}
          disabled={isBusy}
        >
          {isBusy ? 'Öffne ...' : item?.has_opened ? 'Erneut lesen' : '🍾 Flaschenpost öffnen'}
        </button>
      </>
    );
  }

  return (
    <>
      <div className="treasure-map-details__message-box">{openedState.message}</div>
      {openedState.first_open && Number(openedState.awarded || 0) > 0 && (
        <div className="treasure-map-details__status is-success">+{openedState.awarded} XP erhalten</div>
      )}
      {typeof canLoot === 'function' && canLoot(virtualLocation) ? (
        <button type="button" className="treasure-map-details__primary-action" onClick={() => handleLoot?.(virtualLocation)}>
          💰 Hier looten
        </button>
      ) : Number(getCooldownTime?.(virtualLocation.id) || 0) > 0 ? (
        <div className="treasure-map-details__status is-warning">
          Cooldown aktiv · noch {formatCooldown?.(getCooldownTime?.(virtualLocation.id))}
        </div>
      ) : (
        <div className="treasure-map-details__status">Für Loot musst du näher an den Ort herangehen.</div>
      )}
    </>
  );
}

function EmptyState({ onClose = null, variant = 'sidebar' }) {
  return (
    <aside className={`treasure-map-details treasure-map-details--empty ${variant === 'overlay' ? 'treasure-map-details--overlay' : ''}`.trim()}>
      {variant === 'overlay' ? <div className="treasure-map-details__handle" aria-hidden="true" /> : null}
      <div className="treasure-map-details__topbar">
        <div className="treasure-map-details__eyebrow">Details</div>
        {onClose ? (
          <button type="button" className="treasure-map-details__close" onClick={onClose} aria-label="Details schließen">
            ✕
          </button>
        ) : null}
      </div>
      <h3>Kein Punkt ausgewählt</h3>
      <p>
        Klicke auf einen Marker in der Karte oder wähle einen Listeneintrag aus. Die Details erscheinen dann hier statt in einem Popup.
      </p>
    </aside>
  );
}

export function TreasureMapDetailsPanel({
  item,
  selectedItemKey,
  canLoot,
  getCooldownTime,
  formatCooldown,
  handleLoot,
  handlePfandAbgeholt,
  busyBottleMessageId,
  openedBottleMessages,
  onOpenBottleMessage,
  lootMessage,
  variant = 'sidebar',
  onClose = null,
}) {
  if (!item) {
    return <EmptyState onClose={onClose} variant={variant} />;
  }

  const navigationUrl = buildNavigationUrl(item);
  const detailKey = selectedItemKey || `${item.sourceType || 'item'}-${item.id}`;
  const donationLabel = getDonationLabel(item);
  const locationLabel = getLocationLabel(item);

  return (
    <aside
      className={`treasure-map-details ${variant === 'overlay' ? 'treasure-map-details--overlay' : ''}`.trim()}
      data-selected-key={detailKey}
    >
      {variant === 'overlay' ? <div className="treasure-map-details__handle" aria-hidden="true" /> : null}
      <div className="treasure-map-details__topbar">
        <div className="treasure-map-details__eyebrow">Details</div>
        {onClose ? (
          <button type="button" className="treasure-map-details__close" onClick={onClose} aria-label="Details schließen">
            ✕
          </button>
        ) : null}
      </div>
      <div className="treasure-map-details__header">
        <div>
          <h3>{item.title || item.name || 'Unbenannter Punkt'}</h3>
          <div className="treasure-map-details__meta-row">
            <span className={`treasure-map-details__badge is-${item.sourceType || 'location'}`}>
              {item.sourceType === 'donation'
                ? donationLabel
                : item.sourceType === 'bottle_message'
                  ? 'Flaschenpost'
                  : locationLabel}
            </span>
            <span>{item.categoryLabel || item.category || 'Unbekannt'}</span>
          </div>
        </div>
      </div>

      {item.sourceType === 'donation' && item.imageUrl ? (
        <img className="treasure-map-details__photo" src={item.imageUrl} alt={item.title || 'Fundfoto'} />
      ) : null}

      {item.sourceType === 'donation' && item.event ? (
        <div className="treasure-map-details__event-badge">
          <strong>{item.event.title}</strong>
          <span>Offene Event-Quest</span>
        </div>
      ) : null}

      <div className="treasure-map-details__info-list">
        <div>
          <strong>Entfernung</strong>
          <span>{formatDistance(item.distanceMeters)}</span>
        </div>
        {item.sourceType === 'bottle_message' && item.author_name && (
          <div>
            <strong>Von</strong>
            <span>{item.author_name}</span>
          </div>
        )}
        {item.sourceType === 'bottle_message' && item.expires_at && (
          <div>
            <strong>Aktiv bis</strong>
            <span>{formatDate(item.expires_at)}</span>
          </div>
        )}
        {item.sourceType === 'location' && item.sourceLabel && (
          <div>
            <strong>Quelle</strong>
            <span>{item.sourceLabel}</span>
          </div>
        )}
        {item.sourceType === 'donation' && (
          <>
            <div>
              <strong>Gemeldet</strong>
              <span className="treasure-map-details__time-value">
                {formatDateTime(item.created_at || item.date)}
                {formatRelativeDateTime(item.created_at || item.date) && (
                  <small>{formatRelativeDateTime(item.created_at || item.date)}</small>
                )}
              </span>
            </div>
            {shouldShowModifiedAt(item) && (
              <div>
                <strong>Aktualisiert</strong>
                <span className="treasure-map-details__time-value">
                  {formatDateTime(item.modified_at || item.modified)}
                  {formatRelativeDateTime(item.modified_at || item.modified) && (
                    <small>{formatRelativeDateTime(item.modified_at || item.modified)}</small>
                  )}
                </span>
              </div>
            )}
            <div>
              <strong>{isGiveawayDonation(item) ? 'Art' : 'Menge'}</strong>
              <span>{item.amount_type_label || (isGiveawayDonation(item) ? 'Fundsache / zu verschenken' : 'Einzelnes Teil')}</span>
            </div>
            <div>
              <strong>{isGiveawayDonation(item) ? 'Kategorie' : 'Pfandart'}</strong>
              <span>{item.pfand_type_label || donationLabel}</span>
            </div>
            <div>
              <strong>Bestätigungen</strong>
              <span>{formatConfirmationCount(item.confirmation_count)}</span>
            </div>
            <div>
              <strong>Zuletzt bestätigt</strong>
              <span>{formatDateTime(item.last_confirmed_at)}</span>
            </div>
          </>
        )}
      </div>

      {item.sourceType === 'donation' && item.isOlderThan24h && (
        <div className="treasure-map-details__status is-warning">
          Ältere Meldung: Bitte vor der Anfahrt prüfen, ob sie noch plausibel ist.
        </div>
      )}

      {item.sourceType === 'location' && item.notice && (
        <div className="treasure-map-details__status is-warning">
          {item.notice}
        </div>
      )}

      {item.description && <p className="treasure-map-details__description">{item.description}</p>}

      <div className="treasure-map-details__actions">
        {navigationUrl && (
          <a
            className="treasure-map-details__secondary-action"
            href={navigationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            📍 Hin navigieren
          </a>
        )}

        {item.sourceType === 'location' && (
          <LocationActions
            item={item}
            canLoot={canLoot}
            getCooldownTime={getCooldownTime}
            formatCooldown={formatCooldown}
            handleLoot={handleLoot}
          />
        )}

        {item.sourceType === 'donation' && (
          <DonationActions
            item={item}
            handlePfandAbgeholt={handlePfandAbgeholt}
          />
        )}

        {item.sourceType === 'bottle_message' && (
          <BottleMessageActions
            item={item}
            busyBottleMessageId={busyBottleMessageId}
            openedBottleMessages={openedBottleMessages}
            onOpenBottleMessage={onOpenBottleMessage}
            canLoot={canLoot}
            getCooldownTime={getCooldownTime}
            formatCooldown={formatCooldown}
            handleLoot={handleLoot}
          />
        )}
      </div>

      {item.sourceType === 'location' && renderLootFeedback(item, lootMessage)}

      {item.sourceType === 'bottle_message' && renderLootFeedback({ id: item.location_id || `bottle-message-${item.id}` }, lootMessage)}
    </aside>
  );
}

export default TreasureMapDetailsPanel;
